import type { OfflineMutation } from "@/lib/offline/offlineTypes";

const DB_NAME = "domio-cleaning-offline";
const DB_VERSION = 1;

export type ChecklistCacheRecord = {
  key: string;
  payload: unknown;
  updatedAt: number;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("mutations")) {
        db.createObjectStore("mutations", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("photo_blobs")) {
        db.createObjectStore("photo_blobs", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("checklist_cache")) {
        db.createObjectStore("checklist_cache", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Nie udało się otworzyć IndexedDB"));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

export async function putMutation(mutation: OfflineMutation): Promise<void> {
  const db = await openDb();
  try {
    await requestToPromise(db.transaction("mutations", "readwrite").objectStore("mutations").put(mutation));
  } finally {
    db.close();
  }
}

export async function getAllMutations(): Promise<OfflineMutation[]> {
  const db = await openDb();
  try {
    const rows = await requestToPromise(
      db.transaction("mutations", "readonly").objectStore("mutations").getAll(),
    );
    return (rows as OfflineMutation[]).sort((a, b) => a.createdAt - b.createdAt);
  } finally {
    db.close();
  }
}

export async function deleteMutation(id: string): Promise<void> {
  const db = await openDb();
  try {
    await requestToPromise(db.transaction("mutations", "readwrite").objectStore("mutations").delete(id));
  } finally {
    db.close();
  }
}

export async function putPhotoBlob(id: string, blob: Blob): Promise<void> {
  const db = await openDb();
  try {
    await requestToPromise(
      db.transaction("photo_blobs", "readwrite").objectStore("photo_blobs").put({ id, blob }),
    );
  } finally {
    db.close();
  }
}

export async function getPhotoBlob(id: string): Promise<Blob | null> {
  const db = await openDb();
  try {
    const row = await requestToPromise(
      db.transaction("photo_blobs", "readonly").objectStore("photo_blobs").get(id),
    ) as { id: string; blob: Blob } | undefined;
    return row?.blob ?? null;
  } finally {
    db.close();
  }
}

export async function deletePhotoBlob(id: string): Promise<void> {
  const db = await openDb();
  try {
    await requestToPromise(db.transaction("photo_blobs", "readwrite").objectStore("photo_blobs").delete(id));
  } finally {
    db.close();
  }
}

export async function putChecklistCache(key: string, payload: unknown): Promise<void> {
  const db = await openDb();
  try {
    const record: ChecklistCacheRecord = { key, payload, updatedAt: Date.now() };
    await requestToPromise(
      db.transaction("checklist_cache", "readwrite").objectStore("checklist_cache").put(record),
    );
  } finally {
    db.close();
  }
}

export async function getChecklistCache<T>(key: string): Promise<T | null> {
  const db = await openDb();
  try {
    const row = await requestToPromise(
      db.transaction("checklist_cache", "readonly").objectStore("checklist_cache").get(key),
    ) as ChecklistCacheRecord | undefined;
    return (row?.payload as T) ?? null;
  } finally {
    db.close();
  }
}

export async function pendingMutationCount(): Promise<number> {
  const rows = await getAllMutations();
  return rows.length;
}
